import { Button, Card, Col, Form, Row } from "antd";
import { InputField } from "../../../../../../atoms/FormFields";
import { AutoCompleteField } from "../../../../../../atoms/FormFields/AutoComplete";
import { AddressComponent, Place } from "../../../../../Company/ducks/types";
import { SelectFieldTags } from "../../../../../../atoms/FormFields/SelectFieldTags";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { createSubcontractor } from "../../../../ducks/services";
import SuccessMessage from "../../../../../../atoms/Toasts/SuccessMessage";
import { AxiosError } from "axios";
import ErrorMessage from "../../../../../../atoms/Toasts/ErrorMessage";
import { useNavigate } from "react-router-dom";
import AddContact from "../../../../Components/AddContact";
import { useAppDispatch, useAppSelector } from "../../../../../../config/hooks";
import { getCompanyFieldsById } from "../../../../../CompanySettings/TagsCategories/ducks/actions";

interface PropsType {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  companyId: string;
}
interface CategoryType {
  label: string;
  value: string;
  color: string;
}

interface FormValues {
  name: string;
  address: string;
  type: string;
  category_ids: string;
}

interface PopUpFormValues {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  title: string;
  preferred_method_of_contact: string;
}

export default function ClientCompany(props: PropsType) {
  const [contacts, setContacts] = useState<PopUpFormValues[]>([]);
  const { setLoading, companyId } = props;
  const dispatch = useAppDispatch();
  const [googleAddress, setGoogleAddress] = useState<Place | null>(null);
  const [categoryDropDown, setCategoryDropDown] = useState<CategoryType[]>([]);

  const tagItems = useAppSelector((state) => state?.tags?.tagData);

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "SUBCONTRACTORS",
      };

      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  useEffect(() => {
    if (tagItems?.data && tagItems?.data?.length > 0) {
      const dummyArray: CategoryType[] = [];
      tagItems?.data?.map((item: { name: string; id: string; color: string }) =>
        dummyArray.push({
          label: item?.name,
          value: item?.id,
          color: item?.color,
        })
      );
      setCategoryDropDown(dummyArray);
    } else {
      setCategoryDropDown([]);
    }
  }, [tagItems]);

  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();

  const navigate = useNavigate();

  const onSubmit = (val: FormValues) => {
    setLoading(true);

    let city = "";
    let state = "";
    googleAddress?.address_components?.forEach(
      (component: AddressComponent) => {
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }
    );

    const payload = {
      name: val?.name?.trim(),
      address: val?.address,
      city: city,
      state: state,
      type: "company",
      category_ids: val?.category_ids,
      company_id: companyId,
      contact: contacts,
    };

    createSubcontractor(payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Subcontractor added");
        navigate(`/subs/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const saveAddMore = async () => {
    const allValues = getValues();
    const isValid = await trigger();

    if (isValid) {
      setLoading(true);

      const payload = {
        name: allValues?.name?.trim(),
        address: allValues?.address,
        type: "company",
        category_ids: allValues?.category_ids,
        company_id: companyId,
        contact: contacts,
      };
      createSubcontractor(payload)
        .then(() => {
          setLoading(false);
          setValue("address", "");
          SuccessMessage("Subcontractor added");
          reset();
          setContacts([]);
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  return (
    <Form
      className="clientsForm"
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.preventDefault();
      }}
    >
      <Card style={{ paddingTop: "120px" }}>
        <Row gutter={8}>
          <Col span={24}>
            <InputField
              fieldName="name"
              label="Company name"
              control={control}
              defaultValue=""
              iProps={{
                placeholder: "Enter company name",
              }}
              isRequired
              rules={{
                required: "Company name is required.",
              }}
              validate={errors.name && "error"}
              validMessage={errors.name && errors.name.message}
            />

            <AutoCompleteField
              fieldName="address"
              label="Billing address"
              control={control}
              defaultValue=""
              iProps={{
                placeholder: "Search address",
              }}
              onPlaceChange={(place: Place) => setGoogleAddress(place)}
              tooltipTitle="This is where invoices or payment statements are sent."
            />

            <SelectFieldTags
              fieldName="category_ids"
              label="Categories"
              control={control}
              selectOption={categoryDropDown}
              className="bottom_0"
              iProps={{
                placeholder: "Select categories",
                mode: "multiple",
              }}
            />
          </Col>
        </Row>
      </Card>
      <Card>
        <AddContact setContacts={setContacts} contacts={contacts} />
      </Card>
      <div className="fixed_btm_card">
        <Card>
          <div className="centered">
            <Button
              htmlType="button"
              className="outlined-btn"
              onClick={() => navigate(`/subs/${companyId}`)}
            >
              Cancel
            </Button>
            <div style={{ display: "flex", gap: "16px" }}>
              <Button
                htmlType="button"
                className="outlined-btn"
                type="primary"
                onClick={saveAddMore}
              >
                Save & add one more
              </Button>
              <Button htmlType="submit" type="primary" className="mint-btn">
                Save & close
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Form>
  );
}
